import { useCallback, useLayoutEffect } from 'react'
import { Cache, useSWRConfig } from 'swr'

const subscribers = new WeakMap<Cache<unknown>, Map<string, Set<symbol>>>()

// A shared read belongs to its cache entry, not the component whose fetcher
// SWR happened to choose. Only committed, enabled consumers keep it active.
export function useQuerySubscribers(key: string): () => boolean {
  const { cache } = useSWRConfig()
  useLayoutEffect(() => {
    if (!key) return
    const entries = subscribers.get(cache) ?? new Map<string, Set<symbol>>()
    const listeners = entries.get(key) ?? new Set<symbol>()
    subscribers.set(cache, entries)
    entries.set(key, listeners)
    const subscriber = Symbol()
    listeners.add(subscriber)
    return () => {
      listeners.delete(subscriber)
      if (!listeners.size) entries.delete(key)
      if (!entries.size) subscribers.delete(cache)
    }
  }, [cache, key])

  return useCallback(
    () => !!key && !!subscribers.get(cache)?.get(key)?.size,
    [cache, key],
  )
}
